import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogOverlay, DialogPortal } from '@/components/dialog';

import { OfferDetails } from '@/features/offers/types';
import { SellerAcceptEoiForm, ProposedSlot, SellerRejectEoiForm, DeclineFormFields } from './seller';
import { MeetingSlotsCard } from './meeting-slots-card';
import { RejectedCard } from './rejected-card';
import { BuyerPendingCard } from './buyer';
import { useNotifications } from '@/features/notifications';
import { useProposeSlots } from '@/features/offers/calendar/hooks';
import { useOnUpdateOffer } from '@/features/offers/hooks';

type ModalType = 'accept' | 'reject' | 'meeting' | 'info' | null;

type EoiActionModalProps = {
  readonly type: ModalType;
  readonly onClose: () => void;
  readonly offer: OfferDetails;
  readonly mode: 'seller' | 'buyer';
};

type ModalContentProps = {
  readonly type: ModalType;
  readonly onClose: () => void;
  readonly offer: OfferDetails;
  readonly mode: 'seller' | 'buyer';
};
export function EoiActionModal({ type, onClose, offer, mode }: EoiActionModalProps) {
  if (!type) return null;

  return (
    <Dialog open={!!type} onOpenChange={(open) => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <ModalContent type={type} onClose={onClose} offer={offer} mode={mode} />
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

function ModalContent({ type, onClose, offer, mode }: ModalContentProps) {
  switch (type) {
    case 'accept':
      return <AcceptContent offer={offer} onClose={onClose} />;
    case 'reject':
      return <RejectContent offer={offer} onClose={onClose} />;
    case 'meeting':
      return <MeetingContent offer={offer} mode={mode} />;
    case 'info':
      return <InfoContent offer={offer} mode={mode} />;
    default:
      return null;
  }
}

// ─── Accept (seller proposes slots) ──────────────────────────────────────────

function AcceptContent({ offer, onClose }: { offer: OfferDetails; onClose: () => void }) {
  const { t } = useTranslation(['offers']);
  const { add } = useNotifications();
  const { proposeSlots, isLoading } = useProposeSlots();

  const handleSubmit = useCallback(
    async (slots: ProposedSlot[]) => {
      try {
        await proposeSlots({
          offer_id: offer.id,
          slots: slots.map((s) => s.start_time),
        }).unwrap();

        add({
          id: new Date().toString(),
          title: t('Success'),
          description: t('Your availability has been shared with the buyer.'),
        });

        onClose();
      } catch (error) {
        console.error('Error proposing slots:', error);

        add({
          id: new Date().toString(),
          title: t('Error'),
          description: t('Could not share your availability. Please try again.'),
        });
      }
    },
    [offer.id, proposeSlots, onClose]
  );

  return (
    <div className="mt-4">
      <SellerAcceptEoiForm onBack={onClose} onSubmit={handleSubmit} isLoading={isLoading} />
    </div>
  );
}

// ─── Reject (seller declines) ────────────────────────────────────────────────

function RejectContent({ offer, onClose }: { offer: OfferDetails; onClose: () => void }) {
  const companyName = offer.company?.name ?? '';
  const opportunityId = offer.opportunity?.id ?? '';

  const { updateOfferStatus } = useOnUpdateOffer({
    id: offer.id,
    opportunity: opportunityId,
    onClose,
  });

  const handleSubmit = useCallback(
    (data: DeclineFormFields) => {
      updateOfferStatus({
        status: 'rejected',
        feedback_reject: data.feedback_reject,
        internal_notes_reject: data.internal_notes_reject ?? undefined,
      });
    },
    [updateOfferStatus]
  );

  return (
    <div className="mt-4">
      <SellerRejectEoiForm companyName={companyName} onBack={onClose} onSubmit={handleSubmit} />
    </div>
  );
}

// ─── Meeting (buyer selects slot / both view details) ────────────────────────

function MeetingContent({ offer, mode }: { offer: OfferDetails; mode: 'seller' | 'buyer' }) {
  return (
    <div className="mt-4">
      <MeetingSlotsCard offerId={offer.id} mode={mode} />
    </div>
  );
}

// ─── Info (pending, rejected) ────────────────────────────────────────────────

function InfoContent({ offer, mode }: { offer: OfferDetails; mode: 'seller' | 'buyer' }) {
  const status = offer.status.name;
  const companyName = offer.company?.name ?? '';

  if (status === 'pending' && mode === 'buyer') {
    return (
      <div className="mt-4">
        <BuyerPendingCard />
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="mt-4">
        <RejectedCard
          mode={mode}
          companyName={companyName}
          feedbackReject={offer.feedbackReject ?? null}
          ratingComments={offer.rating?.comments ?? null}
          internalNotesReject={offer.internalNotesReject ?? null}
        />
      </div>
    );
  }

  return null;
}
